import { useEffect, useState } from "react";
import { Loader2, MapPin, Globe, Monitor, Calendar } from "lucide-react";
import { Acesso } from "@/types/acesso";
import { listarAcessos, obterEstatisticas } from "@/lib/acessos";

export default function AdminAcessos() {
  const [acessos, setAcessos] = useState<Acesso[]>([]);
  const [estatisticas, setEstatisticas] = useState<{
    total: number;
    porCidade: Record<string, number>;
    porEstado: Record<string, number>;
  } | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregar = async () => {
      setCarregando(true);
      try {
        const [dados, stats] = await Promise.all([
          listarAcessos(100),
          obterEstatisticas(),
        ]);
        setAcessos(dados);
        setEstatisticas(stats);
      } finally {
        setCarregando(false);
      }
    };
    
    carregar();
  }, []);

  const formatarData = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const cidadesOrdenadas = estatisticas
    ? Object.entries(estatisticas.porCidade)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
    : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-black">Acessos</h1>
        <p className="text-sm text-black/40">
          {estatisticas
            ? `${estatisticas.total} acesso${estatisticas.total !== 1 ? "s" : ""} registrado${estatisticas.total !== 1 ? "s" : ""}`
            : "Rastreamento de visitas à landing page"}
        </p>
      </div>

      {/* Estatísticas */}
      {!carregando && estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Total de Acessos */}
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-black/5 flex items-center justify-center">
                <Globe className="w-5 h-5 text-black/40" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{estatisticas.total}</p>
                <p className="text-xs text-black/40">Total de Acessos</p>
              </div>
            </div>
          </div>

          {/* Top 10 Cidades */}
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <h3 className="text-sm font-semibold text-black mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Top 10 Cidades
            </h3>
            <div className="space-y-2">
              {cidadesOrdenadas.map(([cidade, count]) => (
                <div key={cidade} className="flex items-center justify-between text-sm">
                  <span className="text-black/60">{cidade}</span>
                  <span className="font-medium text-black">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lista de Acessos */}
      <div className="bg-white rounded-2xl border border-black/5">
        <div className="p-6 border-b border-black/5">
          <h2 className="text-sm font-semibold text-black">Últimos 100 Acessos</h2>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-black/20" />
          </div>
        ) : acessos.length === 0 ? (
          <div className="p-16 flex flex-col items-center justify-center text-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center">
              <Globe className="w-5 h-5 text-black/20" />
            </div>
            <p className="text-sm font-medium text-black/50">
              Nenhum acesso registrado ainda.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/[0.02] border-b border-black/5">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-black/40 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />
                      Data/Hora
                    </div>
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-black/40 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5" />
                      Localização
                    </div>
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-black/40 uppercase tracking-wider hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-3.5 h-3.5" />
                      Dispositivo
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {acessos.map((acesso) => (
                  <tr key={acesso.id} className="hover:bg-black/[0.01] transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-black/60">
                      {formatarData(acesso.timestamp)}
                    </td>
                    <td className="px-6 py-4 text-sm text-black">
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {acesso.cidade}{acesso.estado ? `, ${acesso.estado}` : ""}
                        </span>
                        <span className="text-xs text-black/40">{acesso.pais}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-black/40 hidden lg:table-cell">
                      <div className="max-w-xs truncate" title={acesso.userAgent}>
                        {acesso.userAgent.includes('Mobile') ? '📱 Mobile' : '💻 Desktop'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
